/** `lobbyStore` (06 §1.4): tables and online players from `/ws/lobby` (04 §9.3). */
import { signal } from '@preact/signals';
import type { MsgKey } from '@tarock/i18n';
import type { LobbyServerMsg, LobbyTable, MaintenanceNotice } from '@tarock/protocol';
import type { ConnStatus } from '../table/source';

/** The lobby signals. */
export const lobbyStore = {
  conn: signal<ConnStatus>('idle'),
  tables: signal<LobbyTable[]>([]),
  online: signal<{ userId: string; name: string }[]>([]),
  /** The site's maintenance notice: seeded from `/api/config`, live while the lobby socket is open (it outlives it). */
  notice: signal<MaintenanceNotice | null>(null),
};

/** A lobby notice: a message key, or one with values ("Closed by an administrator: <reason>"). */
export type LobbyNotice = MsgKey | { key: 'table.exit.closedByAdminReason'; vars: { reason: string } };

/** A notice for the lobby after leaving a table ("The host removed you from the table", …). */
export const lobbyNotice = signal<LobbyNotice | null>(null);

/** Applies one lobby message (a welcome replaces everything; diffs are applied as they come). */
export function applyLobbyMsg(m: LobbyServerMsg): void {
  switch (m.t) {
    case 'lobbyWelcome':
      lobbyStore.tables.value = m.tables;
      lobbyStore.online.value = m.online;
      lobbyStore.notice.value = m.notice;
      return;
    case 'tableUpsert': {
      const rest = lobbyStore.tables.value.filter((t) => t.id !== m.table.id);
      lobbyStore.tables.value = m.table.status === 'closed' ? rest : [...rest, m.table];
      return;
    }
    case 'tableRemove':
      lobbyStore.tables.value = lobbyStore.tables.value.filter((t) => t.id !== m.id);
      return;
    case 'online': {
      const rest = lobbyStore.online.value.filter((u) => u.userId !== m.userId);
      lobbyStore.online.value = m.online ? [...rest, { userId: m.userId, name: m.name }] : rest;
      return;
    }
    case 'notice':
      lobbyStore.notice.value = m.notice;
      return;
    default:
      return;
  }
}

/** Clears the lobby state (sign-out, leaving the lobby). */
export function resetLobbyStore(): void {
  lobbyStore.conn.value = 'idle';
  lobbyStore.tables.value = [];
  lobbyStore.online.value = [];
}
